import type { ThemeConfig } from 'antd';

/**
 * Define la paleta de colores de la aplicación.
 * Estos valores se obtienen de las especificaciones de diseño en Figma
 * y deben usarse para todos los componentes de la interfaz de usuario para garantizar la coherencia.
 *
 * La estructura está organizada por categoría de color (PRIMARY, NEUTRALS, SUCCESS, etc.)
 * y sombra (p. Ej., _10_, _50_, _90_).
 */
export const AppColors = {
  VA: {
    PRIMARY: {
      _10_: '#E5EAF3',
      _20_: '#3699FF',
      _50_: '#003082',
      _90_: '#131523'
    },
    NEUTRALS: {
      WHITE: '#FFFFFF',
      _10_: '#F8F9FA',
      _20_: '#F3F3F3',
      _30_: '#F2F2F2',
      _40_: '#AFAFAF',
      _50_: '#7E7E7E',
      _60_: '#5B5B5B',
      _90_: '#1A1A1A'
    },
    BACKGROUND: {
      _00_: '#FCFCFC',
      _05_: '#F8F9FD',
      _20_: '#F1F1F1',
      _30_: '#CED4DA'
    },
    INFO: {
      _10_: '#D0F3FE',
      _20_: '#B0EAFD',
      _30_: '#89E0FC',
      _50_: '#12C1F8'
    },
    SUCCESS: {
      _10_: '#CFF6EC',
      _20_: '#B0F0DF',
      _30_: '#88E9CF',
      _50_: '#11D29E'
    },
    WARNING: {
      _10_: '#FFF8D2',
      _20_: '#FFDF94',
      _50_: '#FFDE20'
    },
    ERROR: {
      _10_: '#FFD4D9',
      _20_: '#FFB8BF',
      _30_: '#FF95A0',
      _50_: '#FF2A40',
      _60_: '#F87171'
    }
  }
};

/**
 * Objeto de configuración para el tema de Ant Design.
 * Este objeto aplica los `AppColors` personalizados a los tokens de tema globales de Ant Design,
 * asegurando que todos los componentes de AntD utilizados en la aplicación sigan el mismo sistema de diseño.
 *
 * @property {object} token - Define variables de estilo globales como el color primario, el tamaño de la fuente, etc.
 *
 * @property {object} cssVar - Configuración para usar variables CSS.
 *
 * @property {boolean} hashed - Determina si se deben generar nombres de clase hash para los estilos.
 */
const themeConfig: ThemeConfig = {
  token: {
    wireframe: false,
    fontFamily: 'Open Sans, Open Sans Fallback',
    colorPrimary: AppColors.VA.PRIMARY._50_,
    colorInfo: AppColors.VA.INFO._50_,
    colorSuccess: AppColors.VA.SUCCESS._50_,
    colorWarning: AppColors.VA.WARNING._50_,
    colorError: AppColors.VA.ERROR._50_,
    colorLink: AppColors.VA.PRIMARY._20_,
    colorBgBase: AppColors.VA.NEUTRALS.WHITE,
    colorTextBase: AppColors.VA.NEUTRALS._90_,
    colorBorder: AppColors.VA.NEUTRALS._20_,
    fontSize: 14,
    borderRadius: 8
  },
  components: {
    Select: {
      optionSelectedBg: AppColors.VA.NEUTRALS._10_,
      optionActiveBg: AppColors.VA.NEUTRALS._10_
    }
  },
  cssVar: {
    prefix: 'traky',
    key: 'traky-id'
  },
  hashed: false
};

export default themeConfig;
